import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { makeStyles, useTheme } from '@material-ui/styles';
import InfoIcon from '@material-ui/icons/Info';
import ImageIcon from '@material-ui/icons/Image';
import { Theme, Button, Grid, useMediaQuery } from '@material-ui/core';

import { useElementSize } from 'hooks/element-size-hook';

import { useImageViewer } from './image-viewer-hook';
import { ImageViewerToolbar } from './image-viewer-toolbar';
import { ImageViewerPreview } from './image-viewer-preview';
import {
  ButtonFactoryFn,
  ImageViewerData,
  ImageViewerInfoComponent,
} from './types';
import { ImageViewerButtons } from './image-viewer-buttons';

interface Props<T, K> {
  allData: ImageViewerData<T>[];
  isPanning: boolean;
  InfoComponent?: ImageViewerInfoComponent<T, K>;
  defaultIndex?: number;
  toolbarButtons?: (JSX.Element | ButtonFactoryFn<T>)[];
  minHeight?: number;
  autofocus?: boolean;
  customProps?: K;
}

interface StylesProps {
  minHeight?: number;
}

const useStyles = makeStyles<Theme, StylesProps>(() => ({
  root: {
    height: '100%',
    minHeight: ({ minHeight }) => minHeight ?? 500,
  },
  infoContainer: {
    height: '100%',
    padding: '1rem',
    overflowY: 'auto',
    backgourndColor: 'white',
  },
  contentContainer: {
    flex: 1,
    height: 0,
  },
  imageContainer: {
    height: '100%',
    display: 'grid',
    placeItems: 'center',
    position: 'relative',
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  image: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
}));

export const ImageViewerContent = <T, K>({
  allData,
  isPanning,
  InfoComponent,
  defaultIndex,
  toolbarButtons,
  minHeight,
  autofocus = false,
  customProps,
}: Props<T, K>) => {
  const [showInfo, setShowInfo] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const {
    goBackward,
    goForward,
    rotation,
    rotateLeft,
    rotateRight,
    downloadImage,
    zoomIn,
    zoomOut,
    goToIndex,
    onKeyDown,
    data,
    index,
    metadata,
  } = useImageViewer({ allData, defaultIndex });
  const styles = useStyles({ minHeight });
  const theme = useTheme<Theme>();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [contentRef, { height }] = useElementSize();

  const { goBackwardDisabled, goForwardDisabled, itemCount } = metadata;

  useEffect(() => {
    if (!autofocus) {
      return;
    }

    rootRef.current?.focus();
  }, [autofocus]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') {
        return;
      }

      e.preventDefault();
      onKeyDown(e);
    },
    [onKeyDown]
  );

  const handleInfoClick = useCallback(() => {
    setShowInfo((prev) => !prev);
  }, []);

  const buttons = useMemo(() => {
    const buttonsFromProps =
      toolbarButtons?.map((button) =>
        button instanceof Function ? button({ data, index }) : button
      ) ?? [];

    return [
      ...(!isDesktop && InfoComponent
        ? [
            <Button onClick={handleInfoClick}>
              {showInfo ? <ImageIcon /> : <InfoIcon />}
            </Button>,
          ]
        : []),
      ...buttonsFromProps,
    ];
  }, [
    InfoComponent,
    toolbarButtons,
    data,
    handleInfoClick,
    index,
    isDesktop,
    showInfo,
  ]);

  const Content = isDesktop ? (
    <>
      <Grid item xs={InfoComponent ? 8 : 12} className={styles.imageContainer}>
        <ImageViewerButtons
          zoomIn={zoomIn}
          zoomOut={zoomOut}
          rotateRight={rotateRight}
          rotateLeft={rotateLeft}
          downloadImage={downloadImage}
        />
        <ImageViewerPreview
          data={data}
          isPanning={isPanning}
          rotation={rotation}
        />
      </Grid>
      {InfoComponent && data && (
        <Grid item xs={4} className={styles.infoContainer}>
          <InfoComponent data={data} customProps={customProps} />
        </Grid>
      )}
    </>
  ) : (
    <>
      {showInfo && InfoComponent && data ? (
        <Grid item xs={12} className={styles.infoContainer}>
          <InfoComponent data={data} customProps={customProps} />
        </Grid>
      ) : (
        <Grid item xs={12} className={styles.imageContainer}>
          <ImageViewerButtons
            zoomIn={zoomIn}
            zoomOut={zoomOut}
            rotateRight={rotateRight}
            rotateLeft={rotateLeft}
            downloadImage={downloadImage}
          />
          <ImageViewerPreview
            data={data}
            isPanning={isPanning}
            rotation={rotation}
          />
        </Grid>
      )}
    </>
  );

  return (
    <Grid
      container
      direction="column"
      className={styles.root}
      wrap="nowrap"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      ref={rootRef}
    >
      <Grid item>
        <ImageViewerToolbar
          index={index}
          goToIndex={goToIndex}
          itemCount={itemCount}
          handleGoBackward={goBackward}
          handleGoForward={goForward}
          goBackwardDisabled={goBackwardDisabled}
          goForwardDisabled={goForwardDisabled}
          handleInfoClick={handleInfoClick}
          toolbarButtons={buttons}
        />
      </Grid>
      <Grid item className={styles.contentContainer} innerRef={contentRef}>
        <Grid container style={{ height }}>
          {Content}
        </Grid>
      </Grid>
    </Grid>
  );
};
